import { useEffect, useRef, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { Toast } from '@revenuemarket/ui';
import { Image, Modal, ModalProps, Typography, Form, Input, InputRef } from 'antd';

import updateCompany from '~apis/company/updateCompany';
import { AddressModal } from '~components/ui/Modal/AddressModal';
import { COMMON_YUP } from '~constants/yup-scheme';
import { useCompany } from '~hooks/queries/companies/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { formatAddress, parseAddress } from '~utils/common/address';

import TaggedInputs from '../RuleManagement/RuleManageItem/TaggedInput';
interface Props extends ModalProps {
  onComplete?: () => void;
}

const ConfirmCompnayInfoModal: React.FC<Props> = ({ ...props }) => {
  const { companyId } = useCompanyId();

  const { data: companyResponse } = useCompany({ companyId });
  const companyData = companyResponse?.Data?.data;

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [submittable, setSubmittable] = useState(false);

  useEffect(() => {
    if (companyData) {
      form.setFieldsValue({
        name: companyData?.name,
        businessRegNumber: companyData.businessRegNo,
        taxRegistrationNo: companyData?.taxRegistrationNo,
        representatives: companyData?.korRepresentativeName?.split(','),
        addressDetail: companyData?.addressDetail,
        address: companyData?.address,
        btpNo: companyData?.btpNo,
        bzdNo: companyData?.bzdNo,
      });
    }
  }, [companyData, form]);

  const detailAddressInputRef = useRef<InputRef>(null);
  const onFinish = async (values: {
    name: string;
    businessRegNumber: string;
    taxRegistrationNo: string;
    representatives: string[];
    address: string;
    addressDetail: string;
    lotAddress: string;
    btpNo: string;
    bzdNo: string;
    taxContactsName: string;
    taxContactsEmail: string;
  }) => {
    const response = await updateCompany(companyId as string, {
      taxRegistrationNo: values.taxRegistrationNo,
      korRepresentativeName: values?.representatives?.length
        ? values.representatives.join(',')
        : '',
      address: values.address,
      addressDetail: values?.addressDetail ?? '',
      btpNo: values?.btpNo ?? '',
      bzdNo: values?.bzdNo ?? '',
      taxContacts: [
        {
          name: values.taxContactsName,
          email: values.taxContactsEmail,
        },
      ],
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      props.onComplete?.();
      Toast.success('회사 정보가 성공적으로 변경되었습니다.');
    } else {
      Toast.error('회사 정보 변경에 실패했습니다.');
    }
  };

  const valueForCheck = Form.useWatch([], form);
  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      },
    );
  }, [valueForCheck]);

  return (
    <Modal
      open={props.open}
      onCancel={props.onCancel}
      closable
      okText="계속"
      onOk={() => {
        form.submit();
      }}
      okButtonProps={{
        disabled:
          !submittable ||
          values?.representatives?.length === 0 ||
          !values?.btpNo ||
          !values?.bzdNo ||
          !values?.taxContactsEmail ||
          !values?.taxContactsName ||
          !values?.address,
      }}
    >
      <Flex
        gap={24}
        style={{
          padding: '8px 24px 12px 24px',
        }}
        justify="center"
        align="center"
        direction="column"
      >
        <Flex align="center">
          <Image
            alt="barobill-icon"
            src="https://static.clobe.ai/image/clobe-logo-dark-icon.png"
            width={24}
            height={24}
            style={{
              height: 24,
            }}
          />
          <span style={{ color: colorTokens.textDescription }}>..........</span>
          <Image
            alt="barobill-icon"
            src="https://static.clobe.ai/image/barobill-icon.png"
            width={28}
            height={28}
            style={{
              height: 28,
            }}
          />
        </Flex>
        <Flex direction="column" gap={4} align="center">
          <Typography.Title level={5}>우리 회사 정보를 확인해주세요</Typography.Title>
        </Flex>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{
            width: '100%',
          }}
        >
          <Form.Item name="name" label="회사명" required>
            <Input placeholder="회사명을 입력해주세요." disabled />
          </Form.Item>
          <Flex
            gap={16}
            style={{
              width: '100%',
            }}
          >
            <Form.Item
              name="businessRegNumber"
              label="사업자등록번호"
              required
              style={{
                width: '100%',
              }}
            >
              <Input
                disabled
                placeholder="사업자등록번호를 입력해주세요."
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item name="taxRegistrationNo" label="종사업장번호">
              <Input
                style={{
                  width: 100,
                }}
              />
            </Form.Item>
          </Flex>
          <Form.Item label="대표자명" name="representatives" required>
            <TaggedInputs
              placeholder="대표자명 추가"
              tags={values?.representatives || []}
              setTags={(tags) => form.setFieldsValue({ representatives: tags })}
            />
          </Form.Item>
          <Flex direction="column" style={{ marginBottom: 16 }}>
            <Form.Item
              name={'address'}
              label="주소"
              required
              style={{
                marginBottom: 4,
              }}
            >
              <Input
                readOnly
                placeholder="기본 주소"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  overlays.open(({ isOpen, onClose }) => (
                    <AddressModal
                      isOpen={isOpen}
                      onClose={onClose}
                      onComplete={(address) => {
                        form.setFieldsValue({
                          address: formatAddress(parseAddress(address)),
                        });

                        onClose();

                        setTimeout(() => {
                          detailAddressInputRef.current?.focus();
                        }, 500);
                      }}
                    />
                  ));
                }}
              />
            </Form.Item>
            <Form.Item name="addressDetail" noStyle>
              <Input ref={detailAddressInputRef} placeholder="상세 주소" />
            </Form.Item>
          </Flex>
          <Flex
            gap={16}
            style={{
              width: '100%',
            }}
          >
            <Form.Item
              name="taxContactsName"
              label="세금계산서 발급 담당자명"
              required
              style={{
                width: '50%',
              }}
            >
              <Input style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="taxContactsEmail"
              label="세금계산서 발급 이메일"
              required
              rules={[
                {
                  type: 'email',
                  message: '이메일 형식이 올바르지 않습니다.',
                  validator(rule, value, callback) {
                    if (!value) {
                      callback();
                    } else {
                      COMMON_YUP.email
                        .validate(value)
                        .then(() => {
                          callback();
                        })
                        .catch((error) => {
                          callback(error.message);
                        });
                    }
                  },
                },
              ]}
              style={{
                width: '50%',
              }}
            >
              <Input style={{ width: '100%' }} />
            </Form.Item>
          </Flex>
          <Flex gap={16}>
            <Form.Item label="업태" name="btpNo" required>
              <Input />
            </Form.Item>
            <Form.Item label="업종" name="bzdNo" required>
              <Input />
            </Form.Item>
          </Flex>
        </Form>
      </Flex>
    </Modal>
  );
};

export default ConfirmCompnayInfoModal;
